import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format, parseISO } from "date-fns";
import { da } from "date-fns/locale";
import { Trash2, Plus } from "lucide-react";
import AvailabilityEditor, { type DraftAvailability } from "./AvailabilityEditor";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { RRule } from "rrule";

type AvailabilityRow = {
  id: string;
  listing_id: string;
  type: "single" | "range" | "recurring";
  start_at: string;
  end_at: string;
  rrule: string | null;
};

type Booking = {
  id: string;
  start_at: string;
  end_at: string;
};

export default function EditAvailabilityDialog({
  open,
  onOpenChange,
  listingId,
  listingTitle,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  listingId: string;
  listingTitle: string;
}) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [newAvailabilities, setNewAvailabilities] = useState<DraftAvailability[]>([]);

  // Hent eksisterende availability
  const { data: existingAvailability, isLoading } = useQuery({
    queryKey: ["availability", listingId],
    enabled: open && !!listingId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("availability")
        .select("*")
        .eq("listing_id", listingId)
        .order("start_at", { ascending: true });
      
      if (error) throw error;
      return (data ?? []) as AvailabilityRow[];
    },
  });

  // Hent bookinger for denne listing
  const { data: bookings } = useQuery({
    queryKey: ["listingBookings", listingId],
    enabled: open && !!listingId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("bookings")
        .select("id, start_at, end_at, status")
        .eq("listing_id", listingId)
        .in("status", ["pending", "confirmed"])
        .order("start_at", { ascending: true });
      
      if (error) throw error;
      return (data ?? []) as Booking[];
    },
  });

  // Tjek om en availability kan slettes (ikke booket)
  const canDeleteAvailability = (av: AvailabilityRow): boolean => {
    if (!bookings || bookings.length === 0) return true;

    const avStart = new Date(av.start_at);
    const avEnd = new Date(av.end_at);

    // Tjek om nogen booking overlapper med denne availability
    return !bookings.some(booking => {
      const bookingStart = new Date(booking.start_at);
      const bookingEnd = new Date(booking.end_at);
      
      // Check for overlap
      return bookingStart < avEnd && bookingEnd > avStart;
    });
  };

  // Slet availability
  const deleteAvailability = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("availability")
        .delete()
        .eq("id", id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["availability", listingId] });
      qc.invalidateQueries({ queryKey: ["listingAvailability"] });
      toast({
        title: "Tid fjernet",
        description: "Tilgængelighedstiden er blevet fjernet.",
      });
    },
    onError: (e: any) => {
      toast({
        title: "Kunne ikke fjerne tid",
        description: e?.message ?? "Prøv igen.",
        variant: "destructive",
      });
    },
  });

  // Tilføj nye availabilities
  const addAvailabilities = useMutation({
    mutationFn: async (drafts: DraftAvailability[]) => {
      const rows = drafts.map(draft => {
        if (draft.kind === "single") {
          return {
            listing_id: listingId,
            type: "single" as const,
            start_at: new Date(`${draft.date}T${draft.start}`).toISOString(),
            end_at: new Date(`${draft.date}T${draft.end}`).toISOString(),
            rrule: null,
          };
        } else if (draft.kind === "range") {
          return {
            listing_id: listingId,
            type: "range" as const,
            start_at: new Date(`${draft.startDate}T${draft.startTime}`).toISOString(),
            end_at: new Date(`${draft.endDate}T${draft.endTime}`).toISOString(),
            rrule: null,
          };
        } else {
          // recurring
          const [startHour, startMin] = draft.startTime.split(":").map(Number);
          const [endHour, endMin] = draft.endTime.split(":").map(Number);
          
          const dayMap: Record<string, number> = {
            MO: RRule.MO.weekday,
            TU: RRule.TU.weekday,
            WE: RRule.WE.weekday,
            TH: RRule.TH.weekday,
            FR: RRule.FR.weekday,
            SA: RRule.SA.weekday,
            SU: RRule.SU.weekday,
          };
          
          const byweekday = draft.days.map(d => dayMap[d]).filter(x => x !== undefined);
          
          const rrule = new RRule({
            freq: RRule.WEEKLY,
            byweekday,
            dtstart: new Date(`${draft.fromDate}T${draft.startTime}`),
            until: new Date(`${draft.untilDate}T23:59:59`),
          });

          return {
            listing_id: listingId,
            type: "recurring" as const,
            start_at: new Date(`${draft.fromDate}T${draft.startTime}`).toISOString(),
            end_at: new Date(`${draft.untilDate}T${draft.endTime}`).toISOString(),
            rrule: rrule.toString(),
          };
        }
      });

      const { error } = await supabase.from("availability").insert(rows);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["availability", listingId] });
      qc.invalidateQueries({ queryKey: ["listingAvailability"] });
      setNewAvailabilities([]);
      toast({
        title: "Tider tilføjet",
        description: "De nye tilgængelighedstider er blevet tilføjet.",
      });
    },
    onError: (e: any) => {
      toast({
        title: "Kunne ikke tilføje tider",
        description: e?.message ?? "Prøv igen.",
        variant: "destructive",
      });
    },
  });

  const handleSave = () => {
    if (newAvailabilities.length > 0) {
      addAvailabilities.mutate(newAvailabilities);
    }
  };

  const formatAvailability = (av: AvailabilityRow): string => {
    const start = format(parseISO(av.start_at), "d. MMM yyyy HH:mm", { locale: da });
    const end = format(parseISO(av.end_at), "d. MMM yyyy HH:mm", { locale: da });
    
    if (av.type === "recurring" && av.rrule) {
      try {
        const rule = RRule.fromString(av.rrule);
        const days = rule.options.byweekday?.map(d => {
          const dayNames = ["MO", "TU", "WE", "TH", "FR", "SA", "SU"];
          return dayNames[d];
        }).join(", ") ?? "Alle dage";
        return `Gentagende: ${days} - ${start} til ${end}`;
      } catch (e) {
        return `Gentagende: ${start} → ${end}`;
      }
    } else if (av.type === "range") {
      return `Interval: ${start} → ${end}`;
    } else {
      return `Enkeltdag: ${start} → ${end}`;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Rediger tilgængelighedstider</DialogTitle>
          <DialogDescription>
            Administrer tilgængelighedstider for {listingTitle}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Eksisterende tider */}
          <div>
            <h3 className="text-sm font-medium mb-3">Nuværende tilgængelighedstider</h3>
            {isLoading ? (
              <p className="text-sm text-muted-foreground">Henter...</p>
            ) : !existingAvailability || existingAvailability.length === 0 ? (
              <Alert>
                <AlertDescription>
                  Ingen tilgængelighedstider fundet. Tilføj nye tider nedenfor.
                </AlertDescription>
              </Alert>
            ) : (
              <div className="space-y-2">
                {existingAvailability.map((av) => {
                  const canDelete = canDeleteAvailability(av);
                  return (
                    <div
                      key={av.id}
                      className="flex items-center justify-between p-3 border rounded-lg"
                    >
                      <span className="text-sm">{formatAvailability(av)}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteAvailability.mutate(av.id)}
                        disabled={!canDelete || deleteAvailability.isPending}
                        title={!canDelete ? "Kan ikke slettes - der er bookinger i denne periode" : "Fjern"}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
            
            {bookings && bookings.length > 0 && (
              <Alert className="mt-3">
                <AlertDescription className="text-xs">
                  <strong>OBS:</strong> Tider med aktive bookinger kan ikke slettes.
                </AlertDescription>
              </Alert>
            )}
          </div>

          {/* Tilføj nye tider */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Plus className="h-4 w-4" />
              <h3 className="text-sm font-medium">Tilføj nye tilgængelighedstider</h3>
            </div>
            <AvailabilityEditor
              value={newAvailabilities}
              onChange={setNewAvailabilities}
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Luk
            </Button>
            {newAvailabilities.length > 0 && (
              <Button
                onClick={handleSave}
                disabled={addAvailabilities.isPending}
              >
                {addAvailabilities.isPending ? "Gemmer..." : "Gem nye tider"}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
